import * as React from 'react';
import { TouchLink, TouchLinkGroup, useIsMobile } from '@amzn/storm-ui';
import { MessageBundle } from '@amzn/arb-tools';
import { AccountTypeLabel, IA2AccountDropdownList } from '../styles';
import { NoAccountsMessage } from '../NoAccountsMessage';
import { IA2MobileAccountListItem } from '../IA2MobileAccountListItem';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { AccountLabel } from '../AccountLabel';
import {
  AdvertisingAccountContainer,
  AdvertisingAccountLink,
  AdvertisingAccountMarketplaceName,
} from '../IA2AdvertisingAccountItem';
import {
  AccountTypeLabelContainer,
  AccountTypeSectionContainer,
  IA2AccountListItem,
} from '../IA2AccountDropdownTypeSection';
import {
  CentricAccount,
  CentricAccountType,
  CentricAccountTypeName,
} from '@amzn/aac-chrome-internal-api';

type CentricAccountTypeSectionProps = {
  accounts: CentricAccount[];
  label: string;
  type: CentricAccountType;
  selectedEntityId?: string;
  isLastAccountType?: boolean;
};

const getCentricCountryLabel = ({
  accountType,
  account,
  bundle,
}: {
  accountType: CentricAccountType;
  account: CentricAccount;
  bundle: MessageBundle;
}) => {
  //TODO: confirm with UX Manager Account type's country label
  if (accountType === CentricAccountTypeName.MANAGER) {
    return '';
  }
  if (accountType === CentricAccountTypeName.GLOBAL) {
    return bundle.getMessage('account-switcher-multiple-countries');
  }
  if (account.country) {
    return bundle.getMessage(`account-switcher-marketplace-${account.country}`);
  }
  return '';
};

const CentricAdvertisingAccountItem: React.FC<{
  advertisingAccount: CentricAccount;
  countryLabel: string;
  isSelected: boolean;
}> = ({ advertisingAccount, countryLabel, isSelected }) => {
  const contents = (
    <span>
      <AccountLabel label={advertisingAccount.accountName} />
      {countryLabel && (
        <AdvertisingAccountMarketplaceName fontSize="mini">
          {countryLabel}
        </AdvertisingAccountMarketplaceName>
      )}
    </span>
  );

  if (isSelected) {
    return (
      <AdvertisingAccountContainer>{contents}</AdvertisingAccountContainer>
    );
  }

  return (
    <AdvertisingAccountLink
      data-ccx-e2e-id="aac-account-link"
      // TODO: uncomment once account url is available in CentricAccount
      // href={advertisingAccount.url}
      draggable="false"
    >
      {contents}
    </AdvertisingAccountLink>
  );
};

const CentricDesktopAccountDropdownList: React.FC<{
  accounts: CentricAccount[];
  accountType: CentricAccountType;
  selectedEntityId?: string;
  listReference: React.RefObject<HTMLUListElement>;
}> = ({ accounts, selectedEntityId, accountType, listReference }) => {
  const bundle = useAccountDropdownBundle();

  return (
    <IA2AccountDropdownList ref={listReference}>
      {accounts.map((account: CentricAccount) => {
        const isSelected = selectedEntityId === account.accountId;

        return (
          <IA2AccountListItem key={account.accountId} isSelected={isSelected}>
            <CentricAdvertisingAccountItem
              isSelected={isSelected}
              advertisingAccount={account}
              key={account.accountId}
              countryLabel={getCentricCountryLabel({
                accountType,
                account,
                bundle,
              })}
            />
          </IA2AccountListItem>
        );
      })}
    </IA2AccountDropdownList>
  );
};

const CentricMobileAccountDropdownList: React.FC<{
  accounts: CentricAccount[];
  accountType: CentricAccountType;
}> = ({ accounts, accountType }) => {
  const bundle = useAccountDropdownBundle();
  return (
    <TouchLinkGroup>
      {accounts.map((account) => {
        return (
          <TouchLink
            key={account.accountId}
            // TODO: uncomment once account url is available in CentricAccount
            // href={account.url}
            data-ccx-e2e-id="aac-account-link"
            data-takt-id="aac-account-link"
          >
            <IA2MobileAccountListItem
              key={account.accountId}
              accountLabel={account.accountName}
              countryLabel={getCentricCountryLabel({
                accountType,
                account,
                bundle,
              })}
            />
          </TouchLink>
        );
      })}
    </TouchLinkGroup>
  );
};

export const CentricAccountDropdownTypeSection: React.FC<
  CentricAccountTypeSectionProps
> = ({ accounts, label, type, selectedEntityId }) => {
  const isMobile = useIsMobile();
  const listReference = React.useRef<HTMLUListElement>(null);

  return (
    <AccountTypeSectionContainer>
      <AccountTypeLabelContainer>
        <AccountTypeLabel type="h6">{label}</AccountTypeLabel>
      </AccountTypeLabelContainer>
      {(!accounts || accounts.length == 0) && <NoAccountsMessage />}
      {accounts && accounts.length > 0 && !isMobile && (
        <CentricDesktopAccountDropdownList
          selectedEntityId={selectedEntityId}
          accountType={type}
          accounts={accounts}
          listReference={listReference}
        />
      )}
      {accounts && accounts.length > 0 && isMobile && (
        <CentricMobileAccountDropdownList
          accounts={accounts}
          accountType={type}
        />
      )}
    </AccountTypeSectionContainer>
  );
};
